import {
  CaretDown,
  Check,
  GasPump,
  LinkBreak,
  MapPin,
  PhoneOutgoing,
  Tire,
  Truck,
  Wrench,
} from '@phosphor-icons/react';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../utils/UserContext';
import { CustomerWithDriverOnboarding } from '../../../../../../types/apiTypes';
import SupportModal from '../../SupportModal';
import { useMemo, useState } from 'react';
import { isPreActiveCustomer } from '../../home/utils/demoData';

const radiusOptions = [
  {
    miles: 1,
    value: 2,
  },
  {
    miles: 5,
    value: 5,
  },
  {
    miles: 10,
    value: 10,
  },
  {
    miles: 30,
    value: 30,
  },
  {
    miles: 50,
    value: 50,
  },
];

function VendorFilterButtons({
  filterGasStation,
  setFilterGasStation,
  filterTires,
  setFilterTires,
  filterServiceCenter,
  setFilterServiceCenter,
  filterTowing,
  setFilterTowing,
  filterRental,
  setFilterRental,
  selectedRadius,
  setSelectedRadius,
  openRadiusSelect,
  setOpenRadiusSelect,
  toggleOpenRadiusSelect,
  isCompassExpandable,
}: {
  filterGasStation: boolean;
  setFilterGasStation: (filter: boolean) => void;
  filterTires: boolean;
  setFilterTires: (filter: boolean) => void;
  filterServiceCenter: boolean;
  setFilterServiceCenter: (filter: boolean) => void;
  filterTowing: boolean;
  setFilterTowing: (filter: boolean) => void;
  filterRental: boolean;
  setFilterRental: (filter: boolean) => void;
  selectedRadius: number;
  setSelectedRadius: (radius: number) => void;
  openRadiusSelect: boolean;
  setOpenRadiusSelect: (open: boolean) => void;
  toggleOpenRadiusSelect: () => void;
  isCompassExpandable?: boolean;
}) {
  const { screenView } = useScreenView();
  const { userRecord } = useUser();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState<boolean>(false);
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );
  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <>
            <div className="absolute right-16 top-20 z-10 flex space-x-1">
              <div className="relative">
                <button
                  className={`flex items-center space-x-2 rounded-full border-[1px] border-accent bg-[#FDEACB] px-2 py-1 text-[14px] text-black shadow-md`}
                  onClick={() => toggleOpenRadiusSelect()}
                >
                  <MapPin size={24} />
                  <span>{selectedRadius} miles</span>
                  <CaretDown
                    size={16}
                    weight="bold"
                    className={`${openRadiusSelect && 'rotate-180'}`}
                  />
                </button>
                {openRadiusSelect && (
                  <div className="absolute right-0 top-10 flex h-[280px] w-[144px] flex-col gap-y-2 rounded-[4px] bg-white p-2">
                    {radiusOptions.map((radius, idx) => {
                      return (
                        <button
                          key={radius?.value}
                          onClick={() => {
                            setSelectedRadius(radius?.miles);
                            setOpenRadiusSelect(false);
                          }}
                          className={`${selectedRadius === radius?.miles && 'bg-[#FEF6E9]'} flex flex-row items-center justify-between p-3 align-middle`}
                        >
                          <p className="text-[14px] text-primary">
                            {radius?.miles} mile{radius?.miles > 1 ? 's' : ''}
                          </p>
                          {selectedRadius === radius?.miles && (
                            <Check
                              size={24}
                              className="text-[#D08406]"
                              weight="bold"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="absolute right-16 top-10 z-10 flex space-x-1">
              <button
                className={`flex items-center space-x-2 rounded-full px-2 py-1 text-[14px] ${
                  filterGasStation
                    ? 'bg-yellow-500 text-white'
                    : 'bg-primarybg text-gray-500'
                } shadow-md`}
                onClick={() => setFilterGasStation(!filterGasStation)}
              >
                <GasPump size={24} />
                <span>Fuel Stations</span>
              </button>
              <button
                className={`flex items-center space-x-2 rounded-full px-2 py-1 text-[14px] ${
                  filterTires
                    ? 'bg-purple-500 text-white'
                    : 'bg-primarybg text-gray-500'
                } shadow-md`}
                onClick={() => setFilterTires(!filterTires)}
              >
                <Tire size={24} />
                <span>Tire Suppliers</span>
              </button>
              <button
                className={`flex items-center space-x-2 rounded-full px-2 py-1 text-[14px] ${
                  filterServiceCenter
                    ? 'bg-red-500 text-white'
                    : 'bg-primarybg text-gray-500'
                } shadow-md`}
                onClick={() => setFilterServiceCenter(!filterServiceCenter)}
              >
                <Wrench size={24} />
                <span>Repair Shops</span>
              </button>
              <button
                className={`flex items-center space-x-2 rounded-full px-2 py-1 text-[14px] ${
                  filterTowing
                    ? 'bg-[#06b6d4] text-white'
                    : 'bg-primarybg text-gray-500'
                } shadow-md`}
                onClick={() => setFilterTowing(!filterTowing)}
              >
                <LinkBreak size={24} />
                <span>Towing Service</span>
              </button>
              <button
                className={`flex items-center space-x-2 rounded-full px-2 py-1 text-[14px] ${
                  filterRental
                    ? 'bg-[#84cc16] text-white'
                    : 'bg-primarybg text-gray-500'
                } shadow-md`}
                onClick={() => setFilterRental(!filterRental)}
              >
                <Truck size={24} />
                <span>Truck Rental</span>
              </button>
            </div>
            {(userRecord as CustomerWithDriverOnboarding)?.onboardingSteps
              ?.direct_deposit_verified && (
              <button
                onClick={() => setIsSupportModalOpen(true)}
                disabled={isPreActive}
                className={`absolute bottom-6 ${isCompassExpandable ? 'right-[165px]' : 'right-16'} z-40 flex w-[200px] flex-row items-center justify-center space-x-2 rounded-[4px] border-[1px] border-danger bg-white p-2 text-danger hover:cursor-pointer hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-60`}
              >
                <PhoneOutgoing size={24} weight="bold" />
                <span className="text-[16px] font-[600]">
                  Get Emergency Help
                </span>
                <SupportModal
                  isOpen={isSupportModalOpen}
                  isFleetNet
                  onClose={() => setIsSupportModalOpen(false)}
                />
              </button>
            )}
          </>
        );
      case 'tablet':
        return (
          <>
            <div className="absolute right-2 top-4 z-10 grid grid-cols-1 gap-2">
              <button
                className={`flex items-center space-x-2 rounded-full px-2 py-1 text-[14px] ${
                  filterGasStation
                    ? 'bg-yellow-500 text-white'
                    : 'bg-primarybg text-gray-500'
                } shadow-md`}
                onClick={() => setFilterGasStation(!filterGasStation)}
              >
                <GasPump size={24} />
                <span>Fuel Stations</span>
              </button>
              <button
                className={`flex items-center space-x-2 rounded-full px-2 py-1 text-[14px] ${
                  filterTires
                    ? 'bg-purple-500 text-white'
                    : 'bg-primarybg text-gray-500'
                } shadow-md`}
                onClick={() => setFilterTires(!filterTires)}
              >
                <Tire size={24} />
                <span>Tire Suppliers</span>
              </button>
              <button
                className={`flex items-center space-x-2 rounded-full px-2 py-1 text-[14px] ${
                  filterServiceCenter
                    ? 'bg-red-500 text-white'
                    : 'bg-primarybg text-gray-500'
                } shadow-md`}
                onClick={() => setFilterServiceCenter(!filterServiceCenter)}
              >
                <Wrench size={24} />
                <span>Repair Shops</span>
              </button>
              <button
                className={`flex items-center space-x-2 rounded-full px-2 py-1 text-[14px] ${
                  filterTowing
                    ? 'bg-[#06b6d4] text-white'
                    : 'bg-primarybg text-gray-500'
                } shadow-md`}
                onClick={() => setFilterTowing(!filterTowing)}
              >
                <LinkBreak size={24} />
                <span>Towing Service</span>
              </button>
              <button
                className={`flex items-center space-x-2 rounded-full px-2 py-1 text-[14px] ${
                  filterRental
                    ? 'bg-[#84cc16] text-white'
                    : 'bg-primarybg text-gray-500'
                } shadow-md`}
                onClick={() => setFilterRental(!filterRental)}
              >
                <Truck size={24} />
                <span>Truck Rental</span>
              </button>
              <div className="relative">
                <button
                  className={`flex items-center space-x-2 rounded-full border-[1px] border-accent bg-[#FDEACB] px-2 py-1 text-[14px] text-black shadow-md`}
                  onClick={() => toggleOpenRadiusSelect()}
                >
                  <MapPin size={24} />
                  <span>{selectedRadius} miles</span>
                  <CaretDown
                    size={16}
                    weight="bold"
                    className={`${openRadiusSelect && 'rotate-180'}`}
                  />
                </button>
                {openRadiusSelect && (
                  <div className="absolute right-0 top-10 flex h-[280px] w-[144px] flex-col gap-y-2 rounded-[4px] bg-white p-2">
                    {radiusOptions.map((radius, idx) => {
                      return (
                        <button
                          key={radius?.value}
                          onClick={() => {
                            setSelectedRadius(radius?.miles);
                            setOpenRadiusSelect(false);
                          }}
                          className={`${selectedRadius === radius?.miles && 'bg-[#FEF6E9]'} flex flex-row items-center justify-between p-3 align-middle`}
                        >
                          <p className="text-[14px] text-primary">
                            {radius?.miles} mile{radius?.miles > 1 ? 's' : ''}
                          </p>
                          {selectedRadius === radius?.miles && (
                            <Check
                              size={24}
                              className="text-[#D08406]"
                              weight="bold"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            {(userRecord as CustomerWithDriverOnboarding)?.onboardingSteps
              ?.direct_deposit_verified && (
              <button
                onClick={() => setIsSupportModalOpen(true)}
                disabled={isPreActive}
                className={`absolute bottom-6 ${isCompassExpandable ? 'right-[165px]' : 'right-16'} z-40 flex min-w-[150px] flex-row items-center justify-center space-x-1 rounded-[4px] border-[1px] border-danger bg-white p-1 text-danger hover:cursor-pointer hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-60`}
              >
                <PhoneOutgoing size={20} weight="bold" />
                <span className="text-[14px] font-[600]">
                  Get Emergency Help
                </span>
                <SupportModal
                  isOpen={isSupportModalOpen}
                  isFleetNet
                  onClose={() => setIsSupportModalOpen(false)}
                />
              </button>
            )}
          </>
        );
      case 'mobile':
        return (
          <div className="z-10 flex w-full flex-row gap-x-2 overflow-x-auto overflow-y-hidden py-1">
            <button
              className={`flex h-[30px] max-h-[30px] min-w-[125px] items-center space-x-2 rounded-full px-2 py-1 text-[12px] ${
                filterGasStation
                  ? 'bg-yellow-500 text-white'
                  : 'bg-primarybg text-gray-500'
              } shadow-md`}
              onClick={() => setFilterGasStation(!filterGasStation)}
            >
              <GasPump size={24} />
              <span>Fuel Stations</span>
            </button>
            <button
              className={`flex h-[30px] max-h-[30px] min-w-[125px] items-center space-x-2 rounded-full px-2 py-1 text-[12px] ${
                filterTires
                  ? 'bg-purple-500 text-white'
                  : 'bg-primarybg text-gray-500'
              } shadow-md`}
              onClick={() => setFilterTires(!filterTires)}
            >
              <Tire size={24} />
              <span>Tire Suppliers</span>
            </button>
            <button
              className={`flex h-[30px] max-h-[30px] min-w-[125px] items-center space-x-2 rounded-full px-2 py-1 text-[12px] ${
                filterServiceCenter
                  ? 'bg-red-500 text-white'
                  : 'bg-primarybg text-gray-500'
              } shadow-md`}
              onClick={() => setFilterServiceCenter(!filterServiceCenter)}
            >
              <Wrench size={24} />
              <span>Repair Shops</span>
            </button>
            <button
              className={`flex h-[30px] max-h-[30px] min-w-[130px] items-center space-x-2 rounded-full px-2 py-1 text-[12px] ${
                filterTowing
                  ? 'bg-[#06b6d4] text-white'
                  : 'bg-primarybg text-gray-500'
              } shadow-md`}
              onClick={() => setFilterTowing(!filterTowing)}
            >
              <LinkBreak size={24} />
              <span>Towing Service</span>
            </button>
            <button
              className={`flex h-[30px] max-h-[30px] min-w-[125px] items-center space-x-2 rounded-full px-2 py-1 text-[12px] ${
                filterRental
                  ? 'bg-[#84cc16] text-white'
                  : 'bg-primarybg text-gray-500'
              } shadow-md`}
              onClick={() => setFilterRental(!filterRental)}
            >
              <Truck size={24} />
              <span>Truck Rental</span>
            </button>
            {/* Other buttons */}
            <div>
              <button
                className={`flex h-[30px] max-h-[30px] min-w-[140px] flex-grow items-center space-x-2 rounded-full border-[1px] border-accent bg-[#FDEACB] px-2 py-1 text-[14px] text-black shadow-md`}
                onClick={() => toggleOpenRadiusSelect()}
              >
                <MapPin size={24} />
                <span>{selectedRadius} miles</span>
                <CaretDown
                  size={16}
                  weight="bold"
                  className={`${openRadiusSelect && 'rotate-180'}`}
                />
              </button>
              {openRadiusSelect && (
                <div className="absolute right-2 top-28 z-40 flex w-[144px] flex-col gap-y-2 rounded-[4px] bg-white p-2 shadow-lg">
                  {radiusOptions.map((radius, idx) => {
                    return (
                      <button
                        key={radius?.value}
                        onClick={() => {
                          setSelectedRadius(radius?.miles);
                          setOpenRadiusSelect(false);
                        }}
                        className={`${
                          selectedRadius === radius?.miles && 'bg-[#FEF6E9]'
                        } flex flex-row items-center justify-between p-3 align-middle`}
                      >
                        <p className="text-[14px] text-primary">
                          {radius?.miles} mile{radius?.miles > 1 ? 's' : ''}
                        </p>
                        {selectedRadius === radius?.miles && (
                          <Check
                            size={24}
                            className="text-[#D08406]"
                            weight="bold"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
    }
  };
  return <>{renderScreenView(screenView)}</>;
}

export default VendorFilterButtons;
